import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { rideAPI } from '../../lib/api';
import { formatCurrency, formatDateTime, getStatusColor, getStatusText } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { MapPin, Clock, Loader2 } from 'lucide-react';

const RideHistory = () => {
  const [loading, setLoading] = useState(true);
  const [rides, setRides] = useState([]);

  useEffect(() => {
    loadRides();
  }, []);

  const loadRides = async () => {
    try {
      const response = await rideAPI.getHistory();
      setRides(response.data);
    } catch (error) {
      console.error('Error loading rides:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="ride-history-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Ride History
        </h1>

        {rides.length === 0 ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                No rides yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                Your ride history will appear here
              </p>
              <Link to="/rider/book">
                <button className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider px-8 py-3">
                  Book Your First Ride
                </button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {rides.map((ride) => (
              <Link key={ride.id} to={`/rider/ride/${ride.id}`}>
                <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        {/* Status and Date */}
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={`${getStatusColor(ride.status)} font-semibold uppercase text-xs`}>
                            {getStatusText(ride.status)}
                          </Badge>
                          <span className="text-sm text-slate-500 dark:text-slate-400">
                            {formatDateTime(ride.created_at)}
                          </span>
                        </div>

                        {/* Locations */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-slate-600 dark:text-slate-300 truncate">
                              {ride.pickup_location?.address || `${ride.pickup_location?.lat?.toFixed(4)}, ${ride.pickup_location?.lng?.toFixed(4)}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-slate-600 dark:text-slate-300 truncate">
                              {ride.dropoff_location?.address || `${ride.dropoff_location?.lat?.toFixed(4)}, ${ride.dropoff_location?.lng?.toFixed(4)}`}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Fare and Details */}
                      <div className="text-right">
                        <p className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                          {formatCurrency(ride.actual_fare || ride.estimated_fare)}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {ride.distance_miles?.toFixed(1)} mi · {ride.duration_minutes} min
                        </p>
                        {ride.tip_amount > 0 && (
                          <p className="text-sm text-green-600 dark:text-green-400">
                            +{formatCurrency(ride.tip_amount)} tip
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RideHistory;
